import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import uid from '~/features/base/utils/uid';
import {
    fetchControlDeviceRolloutStates,
    setSelectedTabDeviceDetailsSidebar,
} from '~/features/devices/actions/ui/controlDeviceDetailsActions';
import { registerDataInterest, unregisterDataInterest } from '~/features/base/actions/ui/dataInterestActions';
import { triggerDataFetcher } from '~/features/base/actions/ui/dataFetcherActions';
import { entitiesOfDeviceSelector } from '~/features/devices/selectors/controlDeviceRolloutStatusSelectors';
import orderBy from 'lodash/fp/orderBy';
import { FormattedMessage, FormattedTime, FormattedDate } from 'react-intl';
import classNames from 'classnames';
import UpdateEventType from '~/features/updateEvents/components/UpdateEventType';
import {
    ADAPT_ORDER_IN_PROGRESS,
    ASSIGNED, BUNDLE_INSTALL,
    BUNDLE_UPDATE,
    DELIVERABLE_INSTALL,
    DELIVERABLE_UNINSTALL,
    DELIVERABLE_UPDATE,
    DISTRO_UPDATE,
    DOWNLOAD_FINISHED,
    DOWNLOAD_IN_PROGRESS,
    FAILED,
    FILE_DOWNLOAD,
    FINISHED,
    INSTALLATION_FINISHED,
    INSTALLATION_IN_PROGRESS,
    INSTALLATION_PENDING,
    PACKAGE_INSTALL,
    PACKAGE_UPDATE,
    REBOOT_PENDING,
    rolloutStatus,
    SENT_TO_DEVICE,
    SUCCESS,
    UNINSTALL_PACKAGE,
    updateOutcome,
} from '~/features/devices/constants/deviceRolloutStates';

import {
    FETCH_ROLLOUT_STATES_LIMIT,
    POLLING_INTERVAL,
    appProgressStates,
    distroProgressStates,
    deliverableProgressStates,
    bundleProgressStates,
    fileProgressStates,
    uninstallProgressStates,
    rolloutTypeConfig,
} from '~/features/devices/constants/rolloutProgressStates';
import ShortBaseSwVersion from '~/features/baseSwVersions/components/ShortBaseSwVersion';
import DeviceRolloutSubProgress from './DeviceRolloutSubProgress';
import OverlayTrigger from '@rio-cloud/rio-uikit/lib/es/OverlayTrigger';
import Tooltip from '@rio-cloud/rio-uikit/lib/es/Tooltip';
import SteppedProgressBar from '@rio-cloud/rio-uikit/lib/es/SteppedProgressBar';
import { CM4 } from '~/features/devices/constants/deviceTypes';


interface RolloutState {
    type: string;
    isFinished: boolean;
    outcome?: string;
    eventSteps: Array<{ state: string; info?: string; timestamp?: number }>;
    lastUpdated: number;
    deliverableId: string;
    version: string;
    bundleVersion?: string;
    subProgress?: RolloutState[];
}

interface Props {
    serialNumber?: string;
    deviceType?: string;
    autoRefresh?: boolean;
    limit?: number;
    rolloutStates?: RolloutState[];
    rolloutStatesLoading?: boolean;
    registerDataInterest: (name: string, options: any[]) => void;
    unregisterDataInterest: (name: string) => void;
    triggerDataFetcher: () => void;
    setSelectedTabDeviceDetailsSidebar: (tabId: string) => void;
}

const DeviceRolloutStatesContainer: React.FC<Props> = ({
    serialNumber = '',
    deviceType = '',
    autoRefresh = true,
    limit = 0,
    rolloutStates = [],
    registerDataInterest,
    unregisterDataInterest,
    triggerDataFetcher,
    setSelectedTabDeviceDetailsSidebar,
}) => {
    const nameRef = useRef(uid());
    const intervalRef = useRef<NodeJS.Timeout>();

    const checkMultipleDays = (rolloutState: RolloutState): boolean => {
        if (!rolloutState.eventSteps || rolloutState.eventSteps.length < 2) {
            return false;
        }

        const dates = rolloutState.eventSteps
            .filter(step => step.timestamp)
            .map(step => new Date(step.timestamp!).toDateString());

        return new Set(dates).size > 1;
    };

    const getProgressStatesForRollout = (rolloutState: RolloutState): string[] => {
        const baseStates = getProgressStatesForType(rolloutState);
        const eventStates = new Set(rolloutState.eventSteps.map(step => step.state));

        return baseStates.filter(state => {
            if (state !== ADAPT_ORDER_IN_PROGRESS && state !== INSTALLATION_PENDING) {
                return true;
            }
            return eventStates.has(state);
        });
    };

    const getStepNumber = (rolloutState: RolloutState): number => {
        const progressStates = getProgressStatesForRollout(rolloutState);

        if (rolloutState.isFinished && rolloutState.outcome === SUCCESS) {
            return progressStates.length - 1;
        }

        if (rolloutState.isFinished && rolloutState.outcome === FAILED) {
            return -1;
        }

        if (!rolloutState.eventSteps || rolloutState.eventSteps.length === 0) {
            return -1;
        }

        // Find the current step based on the last event in chronological order
        const lastEvent = rolloutState.eventSteps[rolloutState.eventSteps.length - 1];
        const lastStepIndex = progressStates.indexOf(lastEvent.state);

        return lastStepIndex;
    };

    const navigateToTab = (tab?: string) => {
        if (tab) {
            setSelectedTabDeviceDetailsSidebar(tab);
        }
    };

    const getStateLabelDefinition = (state: string, rolloutState: RolloutState) => {
        const progressStates = getProgressStatesForRollout(rolloutState);
        const currentStepNumber = getStepNumber(rolloutState);
        const isCurrentStep = progressStates.indexOf(state) === currentStepNumber;

        const currentStep = rolloutState.eventSteps && [...rolloutState.eventSteps].reverse().find(
            eventStep => eventStep.state === state);
        const currentStepInfo = (currentStep && currentStep.info) || '';
        const currentStepDate = currentStep ? currentStep.timestamp : null;

        return {
            icon: currentStepInfo ? (
                <span style={{ pointerEvents: 'auto' }}>
                    <OverlayTrigger
                        placement='top'
                        overlay={
                            <Tooltip id='tooltip' className='top-right'>
                                {currentStepInfo}
                            </Tooltip>
                        }
                    >
                        {rolloutStatus[state].icon}
                    </OverlayTrigger>
                </span>
            ) : rolloutStatus[state].icon,
            label: (
                <div style={rolloutStatus[state]?.navigationTab ? { pointerEvents: 'auto' } : undefined} className='text-size-11'>
                    <div onClick={() => navigateToTab(rolloutStatus[state]?.navigationTab)}>
                        <span className={rolloutStatus[state]?.navigationTab ? 'rioglyph rioglyph-link padding-right-5' : ''} />
                        <FormattedMessage id={rolloutStatus[state].label} />
                    </div>
                    {currentStepDate && (
                        <div>
                            <OverlayTrigger
                                placement='top'
                                overlay={
                                    <Tooltip id='tooltip-date'>
                                        <FormattedTime value={currentStepDate} year='numeric' month='2-digit' day='2-digit' />
                                    </Tooltip>
                                }
                            >
                                <span>
                                    <FormattedTime value={currentStepDate} hour='numeric' minute='numeric' second='numeric' />
                                </span>
                            </OverlayTrigger>
                        </div>
                    )}
                </div>
            ),
        };
    };



    const compileAppProgressBar = (rolloutState: RolloutState) => {
        const progressStates = getProgressStatesForRollout(rolloutState);
        return progressStates.map(state => getStateLabelDefinition(state, rolloutState));
    };

    const renderOutcome = (rolloutState: RolloutState) => {
        if (!rolloutState.outcome) {
            return null;
        }

        const labelClass = updateOutcome[rolloutState.outcome].className;
        return (
            <span>
                <OverlayTrigger
                    placement='top'
                    overlay={
                        <Tooltip id='tooltip' className='top-right'>
                            <FormattedMessage id={updateOutcome[rolloutState.outcome].tooltip} />
                        </Tooltip>
                    }
                >
                    <span className={classNames('text-size-18', labelClass)}></span>
                </OverlayTrigger>
            </span>
        );
    };


    const renderLabel = (rolloutState: RolloutState) => {
        const renderedUpdateOutcome = renderOutcome(rolloutState);

        return (
            <div>
                <div className='row'>
                    <span>{renderedUpdateOutcome}</span>
                    <span className='text-color-dark padding-left-5'>
                        <FormattedTime value={rolloutState.lastUpdated} year='numeric' month='2-digit' day='2-digit' />
                    </span>
                </div>
                <div className='row padding-top-5 padding-left-25 justify-content-center'>
                    <div className='display-flex'>
                        <div className='margin-right-5'>
                            <UpdateEventType
                                value={rolloutState.type}
                                bundleVersion={rolloutState.type === BUNDLE_UPDATE || rolloutState.type === BUNDLE_INSTALL ?  rolloutState.version : undefined}
                                deliverableId={rolloutState.deliverableId}
                            />
                        </div>
                        <div>
                            <span className='text-color-dark'>{`${rolloutState.deliverableId} - v.`}</span>
                            <span className='text-color-dark'>
                                <ShortBaseSwVersion baseSwVersion={rolloutState.version} />
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    useEffect(() => {
        if (autoRefresh) {
            const fetchRolloutStatesOptions = {
                serialNumber,
                limit: limit || FETCH_ROLLOUT_STATES_LIMIT,
            };

            registerDataInterest(nameRef.current, [
                fetchControlDeviceRolloutStates(fetchRolloutStatesOptions),
            ]);
            triggerDataFetcher();

            intervalRef.current = setInterval(() => {
                triggerDataFetcher();
            }, POLLING_INTERVAL);
        }

        return () => {
            unregisterDataInterest(nameRef.current);
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [autoRefresh, serialNumber, limit, registerDataInterest, unregisterDataInterest, triggerDataFetcher]);

    const [expandedRows, setExpandedRows] = useState<number[]>([]);

    const handleToggleRow = (index: number) => {
        setExpandedRows(prev =>
            prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
        );
    };

    const getProgressStatesForType = (rolloutState: RolloutState): string[] => {
        switch (rolloutState.type) {
            case PACKAGE_UPDATE:
            case PACKAGE_INSTALL:
                return appProgressStates;
            case DISTRO_UPDATE:
                return distroProgressStates;
            case DELIVERABLE_INSTALL:
            case DELIVERABLE_UPDATE:
                return deliverableProgressStates;
            case BUNDLE_UPDATE:
                return bundleProgressStates;
            case FILE_DOWNLOAD:
                return fileProgressStates;
            case UNINSTALL_PACKAGE:
            case DELIVERABLE_UNINSTALL:
                return uninstallProgressStates;
            default:
                return deliverableProgressStates;
        }
    };



    const renderRolloutItem = (rolloutState: RolloutState, key: string | number, index: number) => {
        const labelProgressBar = compileAppProgressBar(rolloutState);
        const selectedStep = getStepNumber(rolloutState);
        const renderedLabel = renderLabel(rolloutState);
        const hasSubEvents = rolloutState.subProgress && rolloutState.subProgress.length > 0;
        const isExpanded = expandedRows.includes(index);
        const hasMultipleDays = checkMultipleDays(rolloutState);

        return (
            <div key={key} className='padding-10'>
                {renderedLabel}
                {hasMultipleDays && (
                    <div className='margin-top-5 margin-bottom-5 padding-5 text-size-11 text-color-warning'>
                        <span className='rioglyph rioglyph-warning-sign margin-right-5' />
                        <FormattedMessage id='intl-msg:deviceRolloutStatus.multipleDaysWarning' />
                    </div>
                )}
                <SteppedProgressBar
                    className='padding-top-10 text-size-10'
                    selectedStepNumber={selectedStep}
                    labels={labelProgressBar}
                    disableFollowingPages={false}
                />
                {hasSubEvents && (
                    <div className='display-flex justify-content-end padding-bottom-10'>
                        <span
                            className='label label-condensed label-default cursor-pointer'
                            onClick={() => handleToggleRow(index)}
                        >
                            <span className={classNames('rioglyph margin-right-3', {
                                'rioglyph-chevron-up': isExpanded,
                                'rioglyph-chevron-down': !isExpanded,
                            })} />
                            {rolloutState.subProgress.length} deliverables
                        </span>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div>
            {orderBy(['lastUpdated'], ['desc'], rolloutStates).map((rolloutState, index) => {
                const isExpanded = expandedRows.includes(index);
                const hasSubEvents = rolloutState.subProgress && rolloutState.subProgress.length > 0;

                return (
                    <div key={index}>
                        {renderRolloutItem(rolloutState, index, index)}
                        {hasSubEvents && isExpanded && (
                            <DeviceRolloutSubProgress subProgress={rolloutState.subProgress} isFinished={rolloutState.isFinished} />
                        )}
                    </div>
                );
            })}
        </div>
    );
};

const mapStateToProps = (state: any, ownProps: any) => ({
    rolloutStates: entitiesOfDeviceSelector(state, ownProps),
});

const mapDispatchToProps = (dispatch: any) => ({
    registerDataInterest: (name: string, options: any[]) => {
        dispatch(registerDataInterest(name, options));
    },
    unregisterDataInterest: (name: string) => {
        dispatch(unregisterDataInterest(name));
    },
    triggerDataFetcher: () => {
        dispatch(triggerDataFetcher());
    },
    setSelectedTabDeviceDetailsSidebar: (tabId: string) => {
        dispatch(setSelectedTabDeviceDetailsSidebar(tabId));
    },
});

export default connect(mapStateToProps, mapDispatchToProps)(DeviceRolloutStatesContainer);
